import { Link } from 'react-router-dom';
import { AdminOfferDetail, OfferResponse } from '../../types/offers';
import OfferStatusChip from './OfferStatusChip';
import { discountLabel, formatAed, formatInstant, formatWindow, itemDiscountText } from './offerReviewModel';

function discountText(o: OfferResponse): string {
  const cap = o.discountType === 'PERCENT' && o.maxDiscountPerBooking != null
    ? ` (max ${formatAed(o.maxDiscountPerBooking)} per booking)`
    : '';
  return `${discountLabel(o)}${cap}`;
}

/** Spec 062 — everything the admin needs to decide, read straight from `AdminOfferDetail`. */
export default function FactsPanel({ detail }: { detail: AdminOfferDetail }) {
  const { offer, baseOffer, garage, priorRejections } = detail;

  return (
    <div className="admin-card offer-facts" data-testid="offer-facts">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Facts</h3>
        <OfferStatusChip status={offer.status} />
      </div>

      {baseOffer && (
        <div className="admin-info-banner info" style={{ marginBottom: '0.75rem' }}>
          Edit of a running offer ({discountText(baseOffer)}, {formatWindow(baseOffer.startsAt, baseOffer.endsAt)}).
          The running offer keeps its terms until this edit is approved.
        </div>
      )}

      <dl className="offer-facts__list">
        <dt>Garage</dt>
        <dd>
          <Link to={`/garages/${garage.id}`} className="admin-link">{garage.name}</Link>{' '}
          <span className={`admin-badge ${garage.status === 'APPROVED' ? 'approved' : 'warning'}`}>{garage.status}</span>{' '}
          {garage.blocked && <span className="admin-badge rejected">Blocked</span>}
        </dd>

        <dt>Garage offers</dt>
        <dd>{garage.liveCount} running · {garage.pendingCount} pending</dd>

        <dt>Window</dt>
        <dd>{formatWindow(offer.startsAt, offer.endsAt)}</dd>

        <dt>Discount</dt>
        <dd data-testid="offer-facts-discount">{discountText(offer)}</dd>

        <dt>Max bookings</dt>
        <dd>
          {offer.maxBookings ?? 'No limit'}
          {offer.bookingsUsed ? ` (${offer.bookingsUsed} used)` : ''}
        </dd>

        <dt>Prior rejections</dt>
        <dd>
          {priorRejections > 0
            ? <span className="admin-badge rejected">{priorRejections}</span>
            : '0'}
        </dd>

        <dt>Submitted</dt>
        <dd>{formatInstant(offer.submittedAt)}</dd>

        {offer.rejectionReason && (
          <>
            <dt>Last rejection</dt>
            <dd>{offer.rejectionReason}</dd>
          </>
        )}
        {offer.suspendReason && (
          <>
            <dt>Suspended for</dt>
            <dd>{offer.suspendReason}</dd>
          </>
        )}
      </dl>

      <h4 className="offer-facts__subhead">Services ({offer.items.length})</h4>
      <table className="admin-table offer-facts__items">
        <thead>
          <tr>
            <th>Service</th>
            <th>Discount</th>
            <th>Before</th>
            <th>After</th>
            <th>Saving</th>
          </tr>
        </thead>
        <tbody>
          {offer.items.map((item) => (
            <tr key={item.offeringId}>
              <td>
                {item.name}
                {!item.bookable && <span className="admin-badge warning" style={{ marginInlineStart: 6 }}>Not bookable</span>}
              </td>
              <td data-testid={`offer-facts-item-discount-${item.offeringId}`}>{itemDiscountText(item, offer)}</td>
              <td><s>{formatAed(item.listPrice)}</s></td>
              <td style={{ fontWeight: 600 }}>{formatAed(item.discountedPrice)}</td>
              <td>{formatAed(Number(item.listPrice) - Number(item.discountedPrice))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
